<template>
    <div class="Conformance">
        <div
            v-if="filteredTraces <= 0"
            class="flex flex-col justify-center items-center q-ma-md"
        >
            <WText :size="18">
                {{ $t('visualization.filters.emptyModel') }}
            </WText>
        </div>

        <div
            v-else-if="hasModels == null"
            class="q-ma-md"
        >
            <QLinearProgress
                indeterminate
                color="primary"
            />
        </div>

        <div
            v-else-if="hasModels === false"
            class="flex flex-col justify-center items-center q-ma-md"
        >
            <WText :size="18">
                {{ $t('visualization.conformance.noModels') }}
            </WText>
            <WText :size="15" class="q-mt-xs">
                {{ $t('visualization.conformance.noModelsUpload') }}
            </WText>
        </div>

        <div
            v-else-if="!modelId"
            class="flex flex-col justify-center items-center q-ma-md"
        >
            <WText :size="18">
                {{ $t('visualization.conformance.chooseBpmn') }}
            </WText>
        </div>

        <div v-else class="row q-col-gutter-md items-stretch">
            <div class="col-12 col-lg-6 flex">
                <q-card flat bordered class="full-height full-width">
                    <q-card-section class="q-pa-sm full-height column">
                        <div class="text-subtitle2 text-weight-bold text-primary q-mb-sm">
                            {{ $t('visualization.conformance.overview') }}
                        </div>
                        <Overview
                            :processId="processId"
                            :modelId="modelId"
                        />
                    </q-card-section>
                </q-card>
            </div>

            <div class="col-12 col-lg-6 flex">
                <q-card flat bordered class="full-height full-width">
                    <q-card-section class="q-pa-sm full-height column">
                        <div class="row items-center justify-between q-mb-sm">
                            <div class="text-subtitle2 text-weight-bold text-primary">
                                {{ $t('visualization.conformance.root') }}
                            </div>
                            <QBtnToggle
                                v-if="showRootCauseToggle"
                                v-model="rootCauseShown"
                                :options="rootCauseShownOptions"
                                toggleColor="primary"
                                textColor="primary"
                                toggleTextColor="white"
                                noCaps
                                unelevated
                                dense
                                size="0.8em"
                                padding="2px 8px"
                                class="border-toggle"
                            />
                        </div>
                        <RootCause
                            :processId="processId"
                            :modelId="modelId"
                            :attribs="attributes"
                            :attribsShown="rootCauseShown"
                        />
                    </q-card-section>
                </q-card>
            </div>

            <div class="col-12">
                <q-card flat bordered>
                    <q-card-section class="q-pa-sm">
                        <div class="text-subtitle2 text-weight-bold text-primary q-mb-sm">
                            {{ $t('visualization.conformance.issues') }}
                        </div>
                        <Issues
                            :processId="processId"
                            :modelId="modelId"
                        />
                    </q-card-section>
                </q-card>
            </div>
        </div>
    </div>
</template>
<script>
import VueTypes from 'vue-types'
import { Overview, Issues, RootCause } from '.'

const BANNED_ROOT_CAUSE_ATTRIBS = ['ACTIVITY', 'TRACEID', 'VariantId']

export default {
    name: 'ConformanceChecking',
    components: {
        Overview,
        Issues,
        RootCause,
    },
    props: {
        projectId: VueTypes.oneOfType([VueTypes.string, VueTypes.number]),
        processId: VueTypes.oneOfType([VueTypes.string, VueTypes.number]),
        attributes: VueTypes.array,
        filteredTraces: VueTypes.number,
        modelId: VueTypes.oneOfType([VueTypes.string, VueTypes.number]),
        hasModels: VueTypes.any.def(undefined),
    },
    data() {
        return {
            rootCauseShown: 2,
        }
    },
    computed: {
        rootCauseUsedAttribCount() {
            const attribs = Array.isArray(this.attributes) ? this.attributes : []
            const usableAttribs = attribs.filter((x) => x && x.type === 'STRING' && !BANNED_ROOT_CAUSE_ATTRIBS.includes(x.name))
            if (usableAttribs.length === 0) return 0

            const relevantAttribs = usableAttribs.filter((x) => x.relevant).slice(0, 3)
            const nonRelevantAttribs = usableAttribs.filter((x) => !x.relevant)
            const attribsUsed = [...relevantAttribs, ...nonRelevantAttribs].slice(0, 4)
            return attribsUsed.length
        },
        rootCauseShownOptions() {
            const count = Math.max(1, Math.min(2, this.rootCauseUsedAttribCount || 1))
            return Array.from({ length: count }, (_, i) => ({ label: String(i + 1), value: i + 1 }))
        },
        showRootCauseToggle() {
            return this.rootCauseShownOptions.length > 1
        },
    },
    watch: {
        rootCauseUsedAttribCount: {
            handler(value) {
                const maxShown = Math.max(1, Math.min(2, value || 1))
                if (this.rootCauseShown > maxShown) {
                    this.rootCauseShown = maxShown
                }
            },
            immediate: true,
        },
    },
}
</script>
<style scoped lang="scss">
.Conformance {
    width: 100%;
    overflow: hidden;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    // QPage in ProcessLayout has padding: 25px (top) + 25px (bottom).
    // Use explicit viewport-based sizing so the graph never ends up with height: 0,
    // while still fitting between the global header and footer.
    // min-height: 400px; // Just in case, to avoid collapsing when the viewport is too small.
    min-height: calc(100vh - $header-height - $footer-height - 90px);
    border-radius: 5px;
}
</style>
